import React, { useEffect, useMemo, useState } from "react";
import {
    Boxes,
    Plus,
    Edit3,
    Trash2,
    RefreshCw,
    Loader2,
    Search,
    X,
    Save,
    Eye,
    EyeOff,
    Euro,
    Award,
    ListChecks,
} from "lucide-react";
import { toast } from "sonner";
import ImageInput from "./ImageInput";
import { inputCls, Field } from "./AdminProducts";

const EMPTY = {
    id: "",
    name: "",
    tagline: "",
    description: "",
    price: "",
    color: "#7A8B76",
    image: "",
    includes: [],
    yield_cups: "",
    badge: "",
    active: true,
};

export default function AdminKits({ apiAuth }) {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(true);
    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setBusy(true);
        try {
            const r = await apiAuth("/admin/catalog/kits");
            setItems(r.data || []);
        } catch {
            toast.error("Erro ao carregar kits");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((i) =>
            `${i.name} ${i.id} ${i.tagline || ""}`.toLowerCase().includes(q),
        );
    }, [items, search]);

    const activeCount = items.filter((i) => i.active !== false).length;

    const handleDelete = async (item) => {
        if (
            !window.confirm(
                `Apagar definitivamente o kit "${item.name}"? Esta ação é irreversível e remove também o stock associado.`,
            )
        )
            return;
        try {
            await apiAuth(`/admin/catalog/kits/${item.id}`, {
                method: "DELETE",
            });
            toast.success(`${item.name} apagado`);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro a apagar");
        }
    };

    const handleToggleActive = async (item) => {
        try {
            await apiAuth(`/admin/catalog/kits/${item.id}`, {
                method: "PUT",
                data: { active: item.active === false },
            });
            toast.success(
                item.active === false ? "Kit ativado" : "Kit oculto da loja",
            );
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro");
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-kits-panel">
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat
                    icon={<Boxes className="h-5 w-5 text-brand-green" />}
                    label="Total kits"
                    value={items.length}
                />
                <Stat
                    tint="green"
                    icon={<Eye className="h-5 w-5 text-brand-green" />}
                    label="Ativos"
                    value={activeCount}
                />
                <Stat
                    tint="orange"
                    icon={<EyeOff className="h-5 w-5 text-brand-orange" />}
                    label="Ocultos"
                    value={items.length - activeCount}
                />
                <button
                    type="button"
                    onClick={() => setEditing("new")}
                    data-testid="btn-new-kit"
                    className="rounded-3xl bg-brand-green hover:bg-brand-green-dark text-white p-5 shadow-pop transition-colors flex flex-col items-start justify-between text-left"
                >
                    <Plus className="h-5 w-5" />
                    <div className="mt-3 text-[11px] uppercase tracking-widest font-bold text-white/85">
                        Adicionar
                    </div>
                    <div className="mt-1 font-display font-medium text-2xl">
                        Novo kit
                    </div>
                </button>
            </section>

            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[240px] max-w-xl">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-ink-soft" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Pesquisar kits…"
                        data-testid="admin-kits-search"
                        className="w-full rounded-full bg-white border-2 border-brand-green/15 focus:border-brand-green px-10 py-2.5 text-sm font-medium text-brand-ink placeholder:text-brand-ink-soft focus:outline-none transition-colors"
                    />
                </div>
                <button
                    type="button"
                    onClick={load}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white hover:bg-brand-green-soft text-brand-ink px-3 py-2 text-xs font-semibold transition-colors ml-auto"
                    data-testid="admin-kits-refresh"
                >
                    <RefreshCw
                        className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`}
                    />
                    Atualizar
                </button>
            </div>

            <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {busy ? (
                    <div className="col-span-full flex justify-center p-16">
                        <Loader2 className="h-6 w-6 animate-spin text-brand-green" />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="col-span-full rounded-3xl bg-white shadow-soft ring-1 ring-brand-green/10 p-16 text-center">
                        <Boxes className="h-10 w-10 text-brand-green/40 mx-auto" />
                        <p className="mt-4 text-sm text-brand-ink-soft">
                            {items.length === 0
                                ? "Ainda não há kits. Adiciona o primeiro!"
                                : "Nenhum kit corresponde à pesquisa."}
                        </p>
                    </div>
                ) : (
                    filtered.map((k) => (
                        <article
                            key={k.id}
                            data-testid={`admin-kit-${k.id}`}
                            className={`rounded-3xl bg-white shadow-soft ring-1 ring-brand-green/10 overflow-hidden flex flex-col transition-opacity ${
                                k.active === false ? "opacity-60" : ""
                            }`}
                        >
                            <div
                                className="aspect-video relative overflow-hidden"
                                style={{ backgroundColor: k.color || "#FFF" }}
                            >
                                {k.image ? (
                                    <img
                                        src={k.image}
                                        alt={k.name}
                                        className="absolute inset-0 w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <Boxes className="h-10 w-10 text-white/70" />
                                    </div>
                                )}
                                {k.badge && (
                                    <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 bg-brand-orange text-white inline-flex items-center gap-1">
                                        <Award className="h-2.5 w-2.5" /> {k.badge}
                                    </span>
                                )}
                                {k.active === false && (
                                    <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 bg-brand-ink/90 text-white inline-flex items-center gap-1">
                                        <EyeOff className="h-2.5 w-2.5" />
                                        Oculto
                                    </span>
                                )}
                            </div>
                            <div className="p-4 flex-1 flex flex-col">
                                <h3 className="font-display font-medium text-brand-ink">
                                    {k.name}
                                </h3>
                                {k.tagline && (
                                    <p className="text-xs font-semibold text-brand-green mt-0.5">
                                        {k.tagline}
                                    </p>
                                )}
                                <p className="text-xs text-brand-ink-soft mt-1 flex-1 line-clamp-2">
                                    {k.description}
                                </p>
                                {(k.includes || []).length > 0 && (
                                    <div className="mt-2 flex items-center gap-1 text-[11px] text-brand-ink-soft">
                                        <ListChecks className="h-3 w-3 text-brand-green" />
                                        <span>
                                            {k.includes.length} itens incluídos
                                        </span>
                                        {k.yield_cups > 0 && (
                                            <span className="ml-2">
                                                · {k.yield_cups} copos
                                            </span>
                                        )}
                                    </div>
                                )}
                                <div className="mt-3 flex items-center justify-between">
                                    <span className="inline-flex items-center gap-1 font-display font-medium text-xl text-brand-green">
                                        <Euro className="h-4 w-4" />
                                        {Number(k.price).toFixed(2)}
                                    </span>
                                    <span className="text-[10px] uppercase tracking-widest font-bold text-brand-ink-soft">
                                        {k.id}
                                    </span>
                                </div>
                                <div className="mt-3 flex items-center gap-2 pt-3 border-t border-brand-green/10">
                                    <button
                                        type="button"
                                        onClick={() => handleToggleActive(k)}
                                        data-testid={`btn-toggle-kit-${k.id}`}
                                        className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                                            k.active === false
                                                ? "bg-brand-cream text-brand-ink hover:bg-brand-ink hover:text-white"
                                                : "bg-brand-green-soft text-brand-green hover:bg-brand-green hover:text-white"
                                        }`}
                                    >
                                        {k.active === false ? (
                                            <EyeOff className="h-3 w-3" />
                                        ) : (
                                            <Eye className="h-3 w-3" />
                                        )}
                                        {k.active === false ? "Oculto" : "Ativo"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setEditing(k)}
                                        data-testid={`btn-edit-kit-${k.id}`}
                                        className="inline-flex items-center gap-1 rounded-full bg-white hover:bg-brand-green-soft border border-brand-green/15 text-brand-ink px-3 py-1.5 text-xs font-semibold transition-colors"
                                    >
                                        <Edit3 className="h-3 w-3" /> Editar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleDelete(k)}
                                        data-testid={`btn-delete-kit-${k.id}`}
                                        className="ml-auto inline-flex items-center gap-1 rounded-full bg-white hover:bg-red-500 border border-red-200 hover:border-red-500 text-red-500 hover:text-white px-3 py-1.5 text-xs font-semibold transition-colors"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </button>
                                </div>
                            </div>
                        </article>
                    ))
                )}
            </section>

            {editing && (
                <KitDialog
                    apiAuth={apiAuth}
                    initial={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={async () => {
                        setEditing(null);
                        await load();
                    }}
                />
            )}
        </div>
    );
}

function KitDialog({ apiAuth, initial, onClose, onSaved }) {
    const isNew = !initial;
    const [form, setForm] = useState(() =>
        initial
            ? {
                  ...EMPTY,
                  ...initial,
                  price: initial.price ?? "",
                  yield_cups: initial.yield_cups ?? "",
                  includes: initial.includes || [],
                  badge: initial.badge || "",
                  active: initial.active !== false,
              }
            : { ...EMPTY },
    );
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const addInclude = () =>
        set("includes", [...(form.includes || []), ""]);
    const updateInclude = (idx, val) =>
        set(
            "includes",
            (form.includes || []).map((x, i) => (i === idx ? val : x)),
        );
    const removeInclude = (idx) =>
        set(
            "includes",
            (form.includes || []).filter((_, i) => i !== idx),
        );

    const submit = async (e) => {
        e.preventDefault();
        const price = parseFloat(form.price);
        if (Number.isNaN(price) || price < 0) {
            toast.error("Preço inválido");
            return;
        }
        const payload = {
            name: form.name.trim(),
            tagline: form.tagline?.trim() || "",
            description: form.description?.trim() || "",
            price,
            color: form.color?.trim() || "#7A8B76",
            image: form.image?.trim() || "",
            includes: (form.includes || []).map((s) => s.trim()).filter(Boolean),
            yield_cups: parseInt(form.yield_cups || "0", 10) || 0,
            badge: form.badge?.trim() || null,
            active: !!form.active,
        };
        setSaving(true);
        try {
            if (isNew) {
                const kid = form.id?.trim();
                await apiAuth("/admin/catalog/kits", {
                    method: "POST",
                    data: kid ? { ...payload, id: kid } : payload,
                });
                toast.success("Kit criado");
            } else {
                await apiAuth(`/admin/catalog/kits/${initial.id}`, {
                    method: "PUT",
                    data: payload,
                });
                toast.success("Kit atualizado");
            }
            await onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro a guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            onClick={onClose}
            data-testid="kit-dialog"
        >
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
                className="w-full max-w-4xl my-8 rounded-3xl bg-white shadow-2xl overflow-hidden"
            >
                <div className="px-6 py-4 bg-brand-green text-white flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Boxes className="h-5 w-5" />
                        <h2 className="font-display font-medium text-lg">
                            {isNew ? "Novo kit" : "Editar kit"}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-white/15 transition-colors"
                        data-testid="kit-dialog-close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="p-6 grid md:grid-cols-[1fr_280px] gap-6 max-h-[75vh] overflow-y-auto">
                    <div className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-3">
                            <Field label="Nome *">
                                <input
                                    required
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => set("name", e.target.value)}
                                    className={inputCls}
                                    data-testid="kit-field-name"
                                />
                            </Field>
                            <Field label="Preço (€) *">
                                <input
                                    required
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={form.price}
                                    onChange={(e) => set("price", e.target.value)}
                                    className={inputCls}
                                    data-testid="kit-field-price"
                                />
                            </Field>
                            <Field label="Tagline">
                                <input
                                    type="text"
                                    value={form.tagline}
                                    onChange={(e) => set("tagline", e.target.value)}
                                    placeholder="Ex: Para começar em casa"
                                    className={inputCls}
                                    data-testid="kit-field-tagline"
                                />
                            </Field>
                            <Field
                                label="Badge"
                                hint="Opcional. Ex: Mais popular"
                            >
                                <input
                                    type="text"
                                    value={form.badge}
                                    onChange={(e) => set("badge", e.target.value)}
                                    className={inputCls}
                                    data-testid="kit-field-badge"
                                />
                            </Field>
                        </div>
                        <Field label="Descrição">
                            <textarea
                                rows={3}
                                value={form.description}
                                onChange={(e) => set("description", e.target.value)}
                                className={`${inputCls} resize-none`}
                                data-testid="kit-field-desc"
                            />
                        </Field>
                        <div className="grid md:grid-cols-2 gap-3">
                            <Field label="Rendimento (copos)">
                                <input
                                    type="number"
                                    min="0"
                                    value={form.yield_cups}
                                    onChange={(e) => set("yield_cups", e.target.value)}
                                    placeholder="40"
                                    className={inputCls}
                                    data-testid="kit-field-yield"
                                />
                            </Field>
                            <Field label="Cor (hex)">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="color"
                                        value={form.color || "#7A8B76"}
                                        onChange={(e) => set("color", e.target.value)}
                                        className="h-10 w-12 rounded-xl border-2 border-brand-green/15 cursor-pointer"
                                        data-testid="kit-field-color-picker"
                                    />
                                    <input
                                        type="text"
                                        value={form.color}
                                        onChange={(e) => set("color", e.target.value)}
                                        className={inputCls}
                                        data-testid="kit-field-color"
                                    />
                                </div>
                            </Field>
                        </div>

                        {isNew && (
                            <Field
                                label="ID (opcional)"
                                hint="Se vazio, é gerado do nome. Ex: kit-familia-plus"
                            >
                                <input
                                    type="text"
                                    value={form.id}
                                    onChange={(e) =>
                                        set(
                                            "id",
                                            e.target.value
                                                .toLowerCase()
                                                .replace(/[^a-z0-9-]/g, "-"),
                                        )
                                    }
                                    className={inputCls}
                                    data-testid="kit-field-id"
                                />
                            </Field>
                        )}

                        <Field
                            label="Inclui"
                            hint="Lista dos itens que o kit contém (ex: 1 Liquidificador, 4 Copos)"
                        >
                            <div className="space-y-2">
                                {(form.includes || []).map((item, idx) => (
                                    <div
                                        key={idx}
                                        className="flex items-center gap-2"
                                    >
                                        <input
                                            type="text"
                                            value={item}
                                            onChange={(e) =>
                                                updateInclude(idx, e.target.value)
                                            }
                                            placeholder="Ex: 4 Copos de marca (500ml)"
                                            className={inputCls}
                                            data-testid={`kit-includes-${idx}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeInclude(idx)}
                                            className="shrink-0 rounded-full bg-red-50 hover:bg-red-500 text-red-500 hover:text-white p-2 transition-colors"
                                            data-testid={`kit-includes-remove-${idx}`}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={addInclude}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-green-soft hover:bg-brand-green hover:text-white text-brand-green px-3 py-1.5 text-xs font-bold transition-colors"
                                    data-testid="kit-add-include"
                                >
                                    <Plus className="h-3 w-3" /> Adicionar item
                                </button>
                            </div>
                        </Field>

                        <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!form.active}
                                onChange={(e) => set("active", e.target.checked)}
                                className="h-4 w-4 rounded border-brand-green/30 text-brand-green focus:ring-brand-green"
                                data-testid="kit-field-active"
                            />
                            <span className="text-sm font-semibold text-brand-ink">
                                Visível na loja pública
                            </span>
                        </label>
                    </div>
                    <div>
                        <ImageInput
                            value={form.image}
                            onChange={(v) => set("image", v)}
                            label="Imagem do kit"
                            testid="kit-field-image"
                        />
                    </div>
                </div>

                <div className="px-6 py-4 bg-brand-cream/60 border-t border-brand-green/10 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center gap-1.5 rounded-full bg-white hover:bg-brand-cream border border-brand-green/15 text-brand-ink px-4 py-2 text-xs font-semibold transition-colors"
                        data-testid="kit-dialog-cancel"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-1.5 rounded-full bg-brand-green hover:bg-brand-green-dark disabled:opacity-50 text-white px-5 py-2 text-xs font-bold transition-colors"
                        data-testid="kit-dialog-save"
                    >
                        {saving ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Save className="h-3.5 w-3.5" />
                        )}
                        {isNew ? "Criar kit" : "Guardar alterações"}
                    </button>
                </div>
            </form>
        </div>
    );
}

const Stat = ({ icon, label, value, tint }) => {
    const bg =
        tint === "green"
            ? "bg-brand-green-soft"
            : tint === "orange"
                ? "bg-brand-orange-soft"
                : "bg-white";
    return (
        <div className={`rounded-3xl ${bg} p-5 shadow-soft ring-1 ring-brand-green/5`}>
            {icon}
            <div className="mt-3 text-[11px] uppercase tracking-widest font-bold text-brand-ink-soft">
                {label}
            </div>
            <div className="mt-1 font-display font-medium text-3xl text-brand-ink">
                {value}
            </div>
        </div>
    );
};
